import type { ReportClassification, ReportClassificationDimension } from './types';
import { findingLabelForSector } from './memberLanguage';
import { hasUnusualResidentDenominator } from './sectorPresentation';

function populationScaleLabel(population: number): string {
  if (population < 10000) return 'Small city — fewer than 10,000 residents';
  if (population < 50000) return 'Small-to-midsize city — 10,000 to 49,999 residents';
  if (population < 100000) return 'Midsize city — 50,000 to 99,999 residents';
  if (population < 250000) return 'Large city — 100,000 to 249,999 residents';
  return 'Major city — 250,000+ residents';
}

export function buildClassification(input: {
  reportReady: boolean;
  population: number | null;
  density: number | null;
  totalMt: number | null;
  perCapitaMt: number | null;
  dominantSector: string | null;
  buildingTransportComparable: boolean;
  buildingMt: number | null;
  transportMt: number | null;
  poolPopulations: number[];
  poolDensities: number[];
  poolTotals: number[];
  poolPerCapita: number[];
}): ReportClassification {
  if (!input.reportReady) {
    return { dimensions: [], suppressedReasons: ['Report not ready — classifications suppressed'] };
  }

  const dims: ReportClassificationDimension[] = [];
  const suppressed: string[] = [];

  if (input.population != null) {
    const scale = populationScaleLabel(input.population);
    dims.push({
      dimensionId: 'population_scale',
      label: scale,
      band: scale,
      eligibleDenominator: 0,
      thresholdNotes: 'Fixed bands: <10k, 10–50k, 50–100k, 100–250k, ≥250k residents',
    });
    if (hasUnusualResidentDenominator(input.population)) {
      dims.push({
        dimensionId: 'unusual_resident_denominator',
        label: 'Resident population is an unusual denominator',
        band: 'unusual_resident_denominator',
        eligibleDenominator: 0,
        thresholdNotes: 'Fixed: resident population below 1,000. Per-resident emissions are de-emphasized. Per-job metrics are not shown until employment data are wired.',
      });
    }
  }

  if (input.density == null) {
    suppressed.push('Density is shown as a fact when available; no density peer band is assigned in this version.');
  }

  suppressed.push('Percentile and peer-comparison classifications are omitted until a method-consistent city cohort exists.');

  if (input.dominantSector) {
    dims.push({
      dimensionId: 'dominant_sector',
      label: `${findingLabelForSector(undefined, input.dominantSector)} is the largest included sector`,
      band: input.dominantSector,
      eligibleDenominator: 0,
      thresholdNotes: 'Maximum among sectors with non-missing emissions at inventory year',
    });
  } else {
    suppressed.push('Largest-sector label suppressed — no valid sector totals');
  }

  if (input.buildingTransportComparable && input.buildingMt != null && input.transportMt != null) {
    if (input.transportMt >= input.buildingMt && input.totalMt != null && input.totalMt > 0) {
      const transportShare = input.transportMt / input.totalMt;
      if (transportShare >= 0.5) {
        dims.push({
          dimensionId: 'transportation_majority',
          label: 'Transportation represents a majority of included emissions',
          band: 'transportation_majority',
          eligibleDenominator: 0,
          thresholdNotes: 'On-road (LDV+HDV) ≥ 50% of included total',
        });
      }
    }
    if (input.buildingMt > input.transportMt && input.totalMt != null && input.totalMt > 0) {
      const buildingShare = input.buildingMt / input.totalMt;
      if (buildingShare >= 0.5) {
        dims.push({
          dimensionId: 'building_energy_majority',
          label: 'Building energy represents a majority of included emissions',
          band: 'building_energy_majority',
          eligibleDenominator: 0,
          thresholdNotes: 'Four building energy sectors ≥ 50% of included total',
        });
      }
    }
  } else {
    suppressed.push('Building-versus-transportation label suppressed — required sectors missing');
  }

  return { dimensions: dims, suppressedReasons: suppressed };
}
